import { ComboBox } from '@/components/ui/combo-box';
import { Option } from '@/components/ui/combo-box/combo-box';
import { useTheme } from '@/hooks/use-theme';
import { ArrowsPointingInIcon } from '@heroicons/react/24/outline';
import { ArrowsPointingOutIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { editor } from 'monaco-editor';
import { lazy, Suspense, useRef, useState } from 'react';

const MonacoEditor = lazy(() => import('@monaco-editor/react'));

interface EditorProps {
  value: string;
  defaultLanguage: string;
  readOnly?: boolean;
  className?: string;
  height?: number;
  onChange?: (
    value: string | undefined,
    editor?: editor.IStandaloneCodeEditor,
    monaco?: any
  ) => void;
  allowFullscreen?: boolean;
  showLanguageSelector?: boolean;
  onMount?: (editor: editor.IStandaloneCodeEditor, monaco: any) => void;
}

const defaultOptions = {
  minimap: {
    enabled: false,
  },
  automaticLayout: true,
};

const languages = [
  { label: 'JavaScript', value: 'javascript' },
  { label: 'JSON', value: 'json' },
  { label: 'Python', value: 'python' },
  { label: 'TypeScript', value: 'typescript' },
];

export function Editor({
  defaultLanguage,
  value,
  readOnly,
  className,
  height,
  onChange,
  allowFullscreen,
  showLanguageSelector = true,
  onMount,
}: EditorProps) {
  const [options] = useState(() => ({
    ...defaultOptions,
    readOnly: readOnly,
  }));
  const [language, setLanguage] = useState<Option | null>();
  const [isExpanded, setIsExpanded] = useState(false);
  const editorRef = useRef<editor.IStandaloneCodeEditor>();
  const monacoRef = useRef<any>();
  const { theme } = useTheme('auto');

  const defaultLanguageValue = languages.find(
    (language) => language.value === defaultLanguage
  );

  const containerClassName = clsx(
    !isExpanded && 'relative mt-2',
    isExpanded && 'h-screen w-full isolate fixed inset-0 z-50'
  );

  function handleExpandClick(): void {
    setIsExpanded(!isExpanded);
    editorRef.current?.layout({} as editor.IDimension);
  }

  function handleMount(editor: editor.IStandaloneCodeEditor, monaco: any) {
    editorRef.current = editor;
    monacoRef.current = monaco;

    onMount?.(editor, monaco);
  }

  function handleChange(value: string | undefined) {
    onChange?.(value, editorRef.current, monacoRef.current);
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div>
        {showLanguageSelector ? (
          <ComboBox
            defaultValue={defaultLanguageValue}
            layout="inline"
            label="Language"
            options={languages}
            name="language"
            value={language}
            onChange={(value: Option | null) => setLanguage(value)}
          />
        ) : null}

        <div className={containerClassName}>
          {allowFullscreen ? (
            <button
              type="button"
              className="absolute right-2 top-2 z-10 rounded p-1 text-gray-500 bg-gray-50 hover:bg-gray-100"
              onClick={handleExpandClick}
            >
              {isExpanded ? (
                <ArrowsPointingInIcon className="w-4 h-4" />
              ) : (
                <ArrowsPointingOutIcon className="w-4 h-4" />
              )}
            </button>
          ) : null}
          <MonacoEditor
            value={value}
            language={language?.value || defaultLanguage}
            options={options}
            theme={theme === 'dark' ? 'vs-dark' : 'vs-light'}
            className={className}
            height={isExpanded ? '100%' : height}
            onChange={handleChange}
            onMount={handleMount}
          />
        </div>
      </div>
    </Suspense>
  );
}
